import React, {useMemo, useState} from 'react';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Select,
    Tooltip,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {ActionButton, CancelButton} from "../../../../../components/common/ActionButtons";
import {useTranslation} from "react-i18next";
import {ActiveDirectoryGroupWithRole, AD_GROUP_ROLE} from "../../../../../models/admin-config.models";
import {ExpandMore} from "@mui/icons-material";
import {isValidRole} from "../ActiveDirectoryGroupsConfiguration/ActiveDirectoryGroupsConfiguration";


interface ChangeGroupRoleDialogProps {
    open: boolean;
    groups: ActiveDirectoryGroupWithRole[];
    onChange: (groups:  ActiveDirectoryGroupWithRole[]) => Promise<void>;
    onClose: () => void;
}

const ChangeGroupRoleDialog = ({ open, groups, onChange, onClose }: ChangeGroupRoleDialogProps) => {
    const {t} = useTranslation();

    const roles = [{
        value: 'USER' as AD_GROUP_ROLE,
        label: 'USER',
    }, {
        value: 'SYSTEM_ADMIN' as AD_GROUP_ROLE,
        label: 'SYSTEM_ADMIN',
    }, {
        value: 'DOCUMENTS_ADMIN' as AD_GROUP_ROLE,
        label: 'DOCUMENTS_ADMIN'
    }];

    const initialGroups = useMemo<ActiveDirectoryGroupWithRole[]>(
        () => groups.map(group => ({
            groupId: group.groupId,
            groupName: group.groupName,
            groupRole: group.groupRole === "NOT_ASSIGNED" ? "" : group.groupRole,
        })),
        [groups]
    );

    const [draftGroups, setDraftGroups] = useState<ActiveDirectoryGroupWithRole[]>(initialGroups);

    const initialGroupsMap = useMemo(
        () => new Map(initialGroups.map(g => [g.groupId, g.groupRole])),
        [initialGroups]
    );

    const isDirty = useMemo(
        () => draftGroups.some(g => initialGroupsMap.get(g.groupId) !== g.groupRole),
        [draftGroups, initialGroupsMap]
    );

    const handleChangeRole = (groupId: number, role: string) => {
        if (!isValidRole(role)) return;

        setDraftGroups(draftGroups.map(group =>
            group.groupId === groupId ? {...group, groupRole: role} : group
        ));
    };

    const handleSaveRoles = async () => {
        const draft = draftGroups
            .filter(group => (initialGroupsMap.get(group.groupId) !== group.groupRole) && (group.groupRole !== ""))
            .map(group => ({ ...group }));

        await onChange(draft);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{ overflow: 'auto', height: '100%'}}
            aria-labelledby="change-assigned-group-role-dialog-title"
            aria-modal="true"
        >
            <DialogTitle
                id="change-assigned-group-role-dialog-title"
                sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
            >
                {t('configuration.adGroups.changeSettingsTitle')}

                <Tooltip title={t('common.close')}>
                    <IconButton onClick={onClose}
                                size="small"
                                aria-label={t('common.close') as string}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <DialogContent>
                {!draftGroups.length && (
                    <Box display="flex" justifyContent="center" alignItems="center" textAlign="center" mt={4} mb={4}>
                        <Typography variant="body1" color="text.secondary">
                            {t('configuration.adGroups.noData')}
                        </Typography>
                    </Box>
                )}

                {(draftGroups.length > 0) && (
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} rowGap={3} alignItems="center" p={4}>
                        {draftGroups.map(group => (
                            <React.Fragment key={group.groupId}>
                                <Typography variant="body1" sx={{ "textOverflow": "ellipsis", "overflow": "hidden", "whiteSpace": "nowrap" }}>
                                    {group.groupName}
                                </Typography>

                                <Select
                                    value={group.groupRole}
                                    displayEmpty
                                    IconComponent={ExpandMore}
                                    onChange={(e) => handleChangeRole(group.groupId, e.target.value)}
                                >
                                    {roles.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </React.Fragment>
                        ))}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <CancelButton onClick={onClose}>
                    {t('common.cancel')}
                </CancelButton>

                <ActionButton
                    onClick={handleSaveRoles}
                    disabled={groups.length === 0 || !isDirty}
                >
                    {t('common.saveChanges')}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeGroupRoleDialog;